// FeatureRequests.jsx — admin-only "pending access requests" inbox at
// /admin/feature-requests. Bidlight's pattern only emails admins when
// users click "Request Access"; this page is a prigmar addition that
// surfaces the same FeatureRequest rows in-app so admins can Grant or
// Dismiss without leaving the browser.
//
// Scope:
//   super-admin    → sees pending requests across every company
//   companyAdmin   → sees only their own company's
//   regular user   → redirected to /dashboard

import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import { get, post } from "../../api";

function fmtTime(t) { if (!t) return '—'; try { return new Date(t).toLocaleString(); } catch { return ''; } }
function fmtPermLabel(perm) {
  // Convert camelCase permission key to "Camel Case" for display.
  return String(perm || '').replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
}

export default function FeatureRequests() {
  const userInfoRedux = useSelector((s) => s.user.userInfo);
  const isAdmin = !!(userInfoRedux?.superAdmin || userInfoRedux?.companyAdmin);
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState('pending');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const refresh = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      const r = await get(`/permissions/requests/inbox?status=${tab}`);
      setRows(((r && r.data) || []));
    } catch (e) { setErr((e && e.message) || 'Failed to load'); }
    finally { setBusy(false); }
  }, [tab]);

  useEffect(() => { if (isAdmin) refresh(); }, [isAdmin, refresh]);
  useEffect(() => { if (!isAdmin) return; const t = setInterval(refresh, 60_000); return () => clearInterval(t); }, [isAdmin, refresh]);

  if (!isAdmin) return <Redirect to="/dashboard" />;

  const act = async (id, kind) => {
    setMsg(null); setErr(null);
    try {
      await post(`/permissions/requests/${id}/${kind}`, {}, false);
      setMsg(`✓ ${kind === 'approve' ? 'granted' : 'dismissed'}`);
      setTimeout(() => setMsg(null), 3500);
      refresh();
    } catch (e) { setErr((e && e.message) || `${kind} failed`); }
  };

  return (
    <div className="container py-4">
      <style>{`
        .fr-card { background: #fff; border: 1px solid #e5e5e5; border-radius: 8px; padding: 14px; margin-bottom: 12px; }
        .fr-table { width: 100%; font-size: 13px; }
        .fr-table th { font-size: 11px; color: #888; text-transform: uppercase; text-align: left; font-weight: 500; border-bottom: 1px solid #f1f1f1; padding: 6px 8px; }
        .fr-table td { padding: 8px 8px; border-bottom: 1px solid #f4f4f4; vertical-align: middle; }
        .fr-tabs { display: flex; gap: 4px; border-bottom: 1px solid #e5e5e5; margin-bottom: 12px; }
        .fr-tab { padding: 6px 12px; cursor: pointer; border: 1px solid transparent; border-bottom: none; border-radius: 6px 6px 0 0; font-size: 12px; color: #525252; }
        .fr-tab.active { background: #fff; border-color: #e5e5e5; color: #15172b; font-weight: 600; }
        .fr-btn { font-size: 12px; padding: 4px 10px; border: 1px solid #ddd; background: #fff; border-radius: 4px; cursor: pointer; color: #525252; }
        .fr-btn:hover:not(:disabled) { background: #f7f9fc; border-color: #bbb; }
        .fr-btn-primary { color: #fff; background: #0F62FE; border-color: #0F62FE; }
        .fr-btn-primary:hover:not(:disabled) { background: #0846b3; }
        .fr-btn-danger { color: #c0392b; border-color: #f1c4c0; }
        .fr-btn-danger:hover:not(:disabled) { background: #fdecea; }
      `}</style>

      <div className="d-flex align-items-baseline justify-content-between mb-3">
        <h3 className="mb-0">Feature Access Requests</h3>
        <div style={{ fontSize: 12, color: '#888' }}>
          {userInfoRedux?.superAdmin ? 'super admin · all tenants' : 'company admin · your team'} · auto-refresh 60s
        </div>
      </div>

      <div className="fr-tabs">
        <div className={"fr-tab" + (tab === 'pending' ? ' active' : '')} onClick={() => setTab('pending')}>Pending</div>
        <div className={"fr-tab" + (tab === 'fulfilled' ? ' active' : '')} onClick={() => setTab('fulfilled')}>Fulfilled</div>
        <div className={"fr-tab" + (tab === 'all' ? ' active' : '')} onClick={() => setTab('all')}>All</div>
      </div>

      {msg && <div className="alert alert-info" style={{ fontSize: 13 }}>{msg}</div>}
      {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}

      <div className="fr-card">
        <table className="fr-table">
          <thead>
            <tr>
              <th>When</th>
              <th>User</th>
              <th>Company</th>
              <th>Requested feature</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (<tr><td colSpan="6" style={{ color: '#999', padding: 20, textAlign: 'center' }}>{busy ? 'Loading…' : 'No requests.'}</td></tr>)}
            {rows.map((r) => (
              <tr key={r._id}>
                <td style={{ whiteSpace: 'nowrap', color: '#888' }}>{fmtTime(r.createdAt || r.date)}</td>
                <td>{r.userEmail || (r.userId ? String(r.userId).slice(-8) : '—')}</td>
                <td>{r.companyName || '—'}</td>
                <td><strong>{fmtPermLabel(r.requestedFeature)}</strong></td>
                <td>{r.fulfilled ? <span style={{ color: '#1e6b3e' }}>fulfilled</span> : <span style={{ color: '#856404' }}>pending</span>}</td>
                <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                  {!r.fulfilled && (
                    <>
                      <button className="fr-btn fr-btn-primary" onClick={() => act(r._id, 'approve')}>Grant</button>
                      <button className="fr-btn fr-btn-danger" style={{ marginLeft: 6 }} onClick={() => act(r._id, 'dismiss')}>Dismiss</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
